import React, { useState, useEffect, useMemo } from "react";
import {
  TrendingUp, TrendingDown, X, ExternalLink, ArrowUpRight, Sparkles,
  AlertTriangle, Loader2, Newspaper, Activity, Target,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from "recharts";
import { Sheet, SheetContent } from "./ui/sheet";
import { Button } from "./ui/button";
import { getBackendUrl } from "../lib/backendUrl";

const ACTION_COLORS = {
  "Strong Buy": { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" },
  "Buy":        { bg: "bg-green-500/20",   text: "text-green-400",   border: "border-green-500/30" },
  "Hold":       { bg: "bg-amber-500/20",   text: "text-amber-400",   border: "border-amber-500/30" },
  "Reduce":     { bg: "bg-orange-500/20",  text: "text-orange-400",  border: "border-orange-500/30" },
  "Exit":       { bg: "bg-red-500/20",     text: "text-red-400",     border: "border-red-500/30" },
  "Watch":      { bg: "bg-blue-500/20",    text: "text-blue-400",    border: "border-blue-500/30" },
};

function fmtINR(v) {
  if (v == null || isNaN(v)) return "--";
  const a = Math.abs(v);
  if (a >= 1e7) return `${v < 0 ? "-" : ""}₹${(Math.abs(v) / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${v < 0 ? "-" : ""}₹${(Math.abs(v) / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

/**
 * Right-side drawer showing a quick stock preview without leaving the current page.
 * - Uses existing /api/stock/{symbol}/detail
 * - "Open Full Detail" routes to StockDetailPage via onOpenFull
 */
export default function StockDetailDrawer({ symbol, market, open, onClose, onOpenFull }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!symbol || !open) return;
    setLoading(true);
    setError(null);
    setData(null);
    const ctrl = new AbortController();
    fetch(`${getBackendUrl()}/api/stock/${encodeURIComponent(symbol)}/detail${market ? `?market=${encodeURIComponent(market)}` : ""}`, {
      signal: ctrl.signal,
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => {
        if (e.name === "AbortError") return;
        setError(e.message || "Failed to load");
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [symbol, market, open]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[520px] bg-[var(--bg-base)] border-l border-white/10 p-0 overflow-y-auto text-white"
        data-testid="stock-drawer"
      >
        <div className="sticky top-0 z-10 bg-[var(--bg-base)] border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">Quick Preview</div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-white p-1 rounded" data-testid="stock-drawer-close">
            <X size={16} />
          </button>
        </div>

        {loading && (
          <div className="p-8 flex items-center justify-center text-[var(--text-muted)]">
            <Loader2 size={22} className="animate-spin text-[var(--brand)]" />
          </div>
        )}

        {error && (
          <div className="p-8 text-center text-red-400 text-sm flex flex-col items-center gap-2">
            <AlertTriangle size={20} /> {error}
          </div>
        )}

        {data && !loading && !error && (
          <DrawerBody data={data} symbol={symbol} market={market} onOpenFull={onOpenFull} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function DrawerBody({ data, symbol, market, onOpenFull }) {
  const { company, price, chart, zones, portfolio, ai_analysis: ai, news } = data;
  const actionStyle = ACTION_COLORS[ai?.action] || ACTION_COLORS["Hold"];
  const isUSD = company?.currency === "USD";
  const ccy = isUSD ? "$" : "₹";

  const chartData = useMemo(() => {
    if (!chart || chart.length === 0) return [];
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 12);
    return chart.filter((d) => new Date(d.date) >= cutoff);
  }, [chart]);

  return (
    <div className="px-5 py-4 space-y-4">
      {/* HERO */}
      <div>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white truncate" style={{ fontFamily: "Chivo" }}>
              {company?.name || symbol}
            </h2>
            <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-1.5 flex-wrap">
              <span className="font-mono">{company?.symbol || symbol}</span>
              {company?.sector && <><span className="text-white/15">·</span><span>{company.sector}</span></>}
              {company?.exchange && <><span className="text-white/15">·</span><span>{company.exchange}</span></>}
            </div>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${actionStyle.bg} ${actionStyle.text} ${actionStyle.border} border whitespace-nowrap`}>
            {ai?.action || "Hold"}
          </span>
        </div>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-2xl font-bold font-mono text-white">
            {ccy}{price?.current?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
          {price?.day_change != null && (
            <span className={`text-sm font-mono ${price.day_change >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
              {price.day_change >= 0 ? "+" : ""}{price.day_change?.toFixed(2)} ({price.day_change_pct?.toFixed(2)}%)
            </span>
          )}
        </div>
      </div>

      {/* MINI CHART */}
      {chartData.length > 0 && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-3" data-testid="stock-drawer-chart">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5">Price · 1Y</div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="drawerGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00D4FF" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#00D4FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 9, fill: "#666" }} axisLine={false} tickLine={false} />
              <YAxis hide domain={["auto", "auto"]} />
              <Tooltip content={<MiniTip ccy={ccy} />} />
              {zones?.buy_zone && (
                <ReferenceArea y1={zones.buy_zone.low} y2={zones.buy_zone.high} fill="#22c55e" fillOpacity={0.05} />
              )}
              {zones?.avg_cost && <ReferenceLine y={zones.avg_cost} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1} />}
              <Area type="monotone" dataKey="close" stroke="#00D4FF" strokeWidth={1.5} fill="url(#drawerGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* AI THESIS */}
      {ai && (
        <div className={`${actionStyle.bg} border ${actionStyle.border} rounded-md p-3.5`} data-testid="stock-drawer-ai">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Sparkles size={13} className={actionStyle.text} />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-white/80">AI Thesis</span>
            </div>
            {ai.confidence && <span className="font-mono text-[10px] text-white/60">{ai.confidence}% conf</span>}
          </div>
          {ai.one_liner && <p className="text-xs text-white/85 italic leading-relaxed mb-2">{ai.one_liner}</p>}

          {(ai.buy_range || ai.reduce_zone) && (
            <div className="grid grid-cols-2 gap-2 mb-2.5">
              {ai.buy_range && (
                <div className="bg-black/25 rounded p-1.5">
                  <div className="text-[9px] text-green-400 uppercase tracking-wider font-semibold">Buy</div>
                  <div className="font-mono text-[11px] text-white">
                    {ccy}{ai.buy_range.low?.toLocaleString()}–{ai.buy_range.high?.toLocaleString()}
                  </div>
                </div>
              )}
              {ai.reduce_zone && (
                <div className="bg-black/25 rounded p-1.5">
                  <div className="text-[9px] text-orange-400 uppercase tracking-wider font-semibold">Reduce</div>
                  <div className="font-mono text-[11px] text-white">
                    {ccy}{ai.reduce_zone.low?.toLocaleString()}–{ai.reduce_zone.high?.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          )}

          {ai.key_bullets?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {ai.key_bullets.slice(0, 4).map((b, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--text-secondary)]">
                  {b}
                </span>
              ))}
            </div>
          )}

          {ai.action_reasoning?.length > 0 && (
            <ul className="space-y-0.5 mt-1.5">
              {ai.action_reasoning.slice(0, 3).map((r, i) => (
                <li key={i} className="text-[11px] text-white/70 flex gap-1.5 leading-snug">
                  <span className="text-cyan-400 flex-shrink-0">▸</span>{r}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* PORTFOLIO CONTEXT */}
      {portfolio?.holding && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-3.5" data-testid="stock-drawer-portfolio">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-2.5 flex items-center gap-1.5">
            <Target size={11} className="text-cyan-400" /> Your Position
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <KV label="Qty" value={`${portfolio.total_qty}`} />
            <KV label="Avg" value={`${ccy}${portfolio.weighted_avg_price?.toLocaleString()}`} />
            <KV label="Value" value={fmtINR(portfolio.total_current)} />
            <KV label="P&L" value={fmtINR(portfolio.combined_pnl)}
                color={portfolio.combined_pnl >= 0}
                sub={`${portfolio.combined_pnl_pct >= 0 ? "+" : ""}${portfolio.combined_pnl_pct}%`} />
            <KV label="Weight" value={`${portfolio.portfolio_weight}%`}
                warn={portfolio.portfolio_weight > 5} />
            <KV label="Sector" value={`${portfolio.sector_exposure}%`}
                warn={portfolio.sector_overexposed} />
          </div>
          {portfolio.is_duplicate && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-300 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1">
              <AlertTriangle size={11} /> Held in {portfolio.duplicate_accounts} accounts
            </div>
          )}
        </div>
      )}

      {/* NEWS — top 4 */}
      {news && news.length > 0 && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-3.5" data-testid="stock-drawer-news">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-2 flex items-center gap-1.5">
            <Newspaper size={11} className="text-cyan-400" /> Recent News
          </div>
          <div className="space-y-1.5">
            {news.slice(0, 4).map((n, i) => (
              <a key={i} href={n.link} target="_blank" rel="noopener noreferrer"
                className="block p-2 bg-[var(--bg-base)] rounded border border-white/5 hover:border-white/15 group">
                <div className="text-xs text-white group-hover:text-[var(--brand)] line-clamp-2 leading-snug">{n.title}</div>
                {(n.publisher || n.published) && (
                  <div className="flex gap-1.5 mt-1 text-[10px] text-[var(--text-muted)]">
                    {n.publisher && <span>{n.publisher}</span>}
                    {n.published && <span>· {n.published}</span>}
                  </div>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <Button onClick={() => onOpenFull(symbol, market)}
        className="w-full bg-[var(--brand)] hover:bg-[var(--brand)]/90 gap-1.5 text-xs font-medium"
        data-testid="stock-drawer-open-full">
        Open Full Detail <ArrowUpRight size={13} />
      </Button>
    </div>
  );
}

function KV({ label, value, color, sub, warn }) {
  return (
    <div className="p-1.5 bg-[var(--bg-base)] rounded border border-white/5">
      <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">{label}</div>
      <div className={`font-mono text-xs mt-0.5 ${warn ? "text-amber-400" : color === true ? "text-[var(--positive)]" : color === false ? "text-[var(--negative)]" : "text-white"}`}>
        {value} {sub && <span className="text-[9px] opacity-60">{sub}</span>}
      </div>
    </div>
  );
}

function MiniTip({ active, payload, ccy }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded p-1.5 text-[10px]">
      <div className="text-[var(--text-muted)]">{d.date}</div>
      <div className="font-mono text-white">{ccy}{d.close?.toLocaleString()}</div>
    </div>
  );
}
